/*
 * Store script: Find a bit (snippets/bit-finder.liquid). Three selects (type, thread or shank, size) build a filtered
 * collection URL from the options JSON in the page (the shop metafield catalog.finder, written by
 * catalog/shopify/load.py --steps finder). No fetch, no backend. The button says how many products match the choice,
 * quick links show their counts. A parameter only filters once its field is a filter in the Search & Discovery app.
 */

const unique = (values) => [...new Set(values.filter((v) => v !== null && v !== undefined && v !== ''))];

// Filter values read inches first ("3-1/2 in (89 mm)"); older ones millimetres first ("89 mm · 3-1/2 in"). OEMs list
// one nominal size at slightly different metric sizes, so each nominal size is offered once, inches first, with every
// stored value behind it.
const parse = (value) => {
  const metricFirst = String(value).match(/^([\d.]+) mm · (.+)$/);
  if (metricFirst) return { nominal: metricFirst[2], mm: Number(metricFirst[1]) };
  const match = String(value).match(/^(.*?) \(([\d.]+) mm\)$/);
  if (match) return { nominal: match[1], mm: Number(match[2]) };
  const lead = String(value).match(/[\d.]+/);
  return { nominal: String(value), mm: lead ? Number(lead[0]) : null };
};

// encodeURIComponent turns a comma inside a value into %2C, which Shopify keeps as part of that one value; a bare
// comma would split it into two.
const param = (name, value) => `${name}=${encodeURIComponent(value)}`;

const init = (root) => {
  if (root.dataset.bitFinderReady) return;
  root.dataset.bitFinderReady = 'true';

  const options = JSON.parse(root.querySelector('[data-bit-finder-options]')?.textContent || 'null') || {};
  const icons = JSON.parse(root.querySelector('[data-bit-finder-icons]')?.textContent || '{}');
  const typeSelect = root.querySelector('[data-bit-finder-type]');
  const keySelect = root.querySelector('[data-bit-finder-thread]');
  const keyField = root.querySelector('[data-bit-finder-key-field]');
  const keyLabel = root.querySelector('[data-bit-finder-key-label]');
  const sizeSelect = root.querySelector('[data-bit-finder-size]');
  const sizeField = root.querySelector('[data-bit-finder-size-field]');
  const sizeLabel = root.querySelector('[data-bit-finder-size-label]');
  const goButton = root.querySelector('[data-bit-finder-go]');
  const anyLabel = root.dataset.anyLabel || 'Any';
  const threadLabel = root.dataset.threadLabel || 'Thread';
  const buttonLabel = goButton.textContent.trim();

  // Options may name a drawing (data-icon); the theme's dropdown shows it (snippets/spec-dropdown.liquid) and is
  // rebuilt whenever the options change. Plain selects ignore it.
  const option = (value, label) => {
    const element = new Option(label, value);
    if (icons[value]) element.dataset.icon = icons[value];
    return element;
  };
  const fill = (select, entries) => {
    select.replaceChildren(option('', anyLabel), ...entries.map(([value, label]) => option(value, label)));
    window.specDropdown?.build(select);
  };
  const family = (type) => options[type || typeSelect.value] || { products: [], key: null, sizeKey: null };
  const withKey = (current, key) => current.products.filter((p) => !key || p[0].includes(key));

  // Nominal size -> every stored value behind it, for the products that carry the key.
  const sizeGroups = (current, key) => {
    const groups = new Map();
    unique(withKey(current, key).flatMap((p) => p[1])).forEach((value) => {
      const { nominal, mm } = parse(value);
      const group = groups.get(nominal) || { values: [], mms: [] };
      group.values.push(value);
      if (mm !== null && value !== nominal) group.mms.push(mm);
      groups.set(nominal, group);
    });
    return [...groups.entries()]
      .map(([nominal, group]) => {
        const mms = unique(group.mms).sort((a, b) => a - b);
        return {
          nominal,
          values: group.values,
          label: mms.length ? `${nominal} (${mms.join(' / ')} mm)` : nominal,
          order: mms.length ? mms[0] : parse(nominal).mm ?? Number.MAX_SAFE_INTEGER,
        };
      })
      .sort((a, b) => a.order - b.order || a.nominal.localeCompare(b.nominal));
  };
  const sizeValues = (target, key, size) => {
    const group = sizeGroups(target, key).find((g) => g.nominal === size || g.values.includes(size));
    return group ? group.values : [size];
  };

  // How many products the choice matches: the key narrows the family, the size narrows that.
  const matches = (type, key, size) => {
    const target = family(type);
    let products = withKey(target, key);
    if (size && target.sizeKey) {
      const values = sizeValues(target, key, size);
      products = products.filter((p) => p[1].some((value) => values.includes(value)));
    }
    return products.length;
  };
  const countLabel = (count) => {
    const template = count === 1 ? root.dataset.countLabelOne : root.dataset.countLabel;
    return template && count > 0 ? template.replace('{count}', count) : buttonLabel;
  };
  const chosenKey = () => (keyField.hidden ? '' : keySelect.value);
  const chosenSize = () => (sizeField.hidden ? '' : sizeSelect.value);
  const refreshButton = () => {
    goButton.textContent = countLabel(matches(typeSelect.value, chosenKey(), chosenSize()));
  };
  const refreshSizes = () => {
    const current = family();
    sizeField.hidden = !current.sizeKey;
    if (current.sizeKey) {
      sizeLabel.textContent = current.sizeLabel;
      fill(sizeSelect, sizeGroups(current, keySelect.value).map((group) => [group.nominal, group.label]));
    }
    refreshButton();
  };
  const refresh = () => {
    const current = family();
    keyField.hidden = !current.key;
    if (current.key) keyLabel.textContent = current.key === 'thread' ? threadLabel : current.keyLabel;
    const keys = current.key ? unique(current.products.flatMap((p) => p[0])) : [];
    fill(keySelect, keys.sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).map((key) => [key, key]));
    refreshSizes();
  };
  const go = (type, key, size) => {
    const target = family(type);
    const params = [];
    if (key && target.key === 'thread') params.push(param('filter.p.m.catalog.threads', key));
    if (key && target.key && target.key !== 'thread') params.push(param(`filter.v.m.spec.${target.key}`, key));
    if (size && target.sizeKey) {
      sizeValues(target, key, size).forEach((value) => params.push(param(`filter.v.m.spec.${target.sizeKey}`, value)));
    }
    window.location.href = `${root.dataset.collectionsRoot}${type}${params.length ? '?' + params.join('&') : ''}`;
  };

  typeSelect.addEventListener('change', refresh);
  keySelect.addEventListener('change', refreshSizes);
  sizeSelect.addEventListener('change', refreshButton);
  goButton.addEventListener('click', () => go(typeSelect.value, chosenKey(), chosenSize()));
  root.querySelectorAll('[data-bit-finder-quick]').forEach((button) => {
    const [type, key, size] = button.dataset.bitFinderQuick.split('|');
    const count = matches(type, key, size);
    if (count) {
      const badge = document.createElement('span');
      badge.className = 'bit-finder__count';
      badge.textContent = count;
      button.append(badge);
    }
    button.addEventListener('click', () => go(type, key, size));
  });
  window.specDropdown?.build(typeSelect);
  refresh();
};

const initAll = (scope) => scope.querySelectorAll('[data-bit-finder]').forEach(init);

initAll(document);
// The theme editor re-renders sections and blocks in place.
document.addEventListener('shopify:section:load', (event) => initAll(event.target));
document.addEventListener('shopify:block:select', (event) => initAll(event.target));
